import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import type {
  AuthenticatedUserContract,
  Locale,
  ReplicaSnapshotPageContract,
  SessionContract,
  StudentReplicaContract,
} from "@chams-pay/contracts";
import { directionFor, translate } from "@chams-pay/contracts";
import {
  OfflineUnlockError,
  forgetInstallationIdentity,
  installationId,
  installationPublicKey,
  lastAuthenticatedIdentifier,
  rememberInstallationDeviceCode,
  rememberInstallationId,
  rememberLastAuthenticatedIdentifier,
  signInstallationValue,
  type PasskeyUnlockMaterial,
} from "@chams-pay/local-store";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiRequestError,
  INSTALLATION_REVOKED_EVENT,
  SERVER_SESSION_INVALID_EVENT,
  SESSION_RESTORE_TIMEOUT_MS,
  apiFetch,
  decodeBase64Url,
  deviceFetch,
  encodeBase64Url,
  json,
  localStore,
} from "./client.js";
import {
  OFFLINE_UPDATE_READY_EVENT,
  activateWaitingOfflineUpdate,
} from "../register-offline-shell.js";
import { requestBrowserStorageReadiness } from "./storage-readiness.js";

export type Gate = "loading" | "auth" | "installation" | "offline-setup" | "locked" | "workspace";
export type ServerSessionState = "restoring" | "ready" | "expired" | "unreachable";

const IDLE_LOCK_MS = 15 * 60 * 1_000;
const SNAPSHOT_PAGE_SIZE = 50;
const UPDATE_RESTART_SESSION_KEY = "chams:update-restart";

function updateRestartRequested(): boolean {
  try {
    return sessionStorage.getItem(UPDATE_RESTART_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

function rememberUpdateRestart(requested: boolean): void {
  try {
    if (requested) sessionStorage.setItem(UPDATE_RESTART_SESSION_KEY, "true");
    else sessionStorage.removeItem(UPDATE_RESTART_SESSION_KEY);
  } catch {
    // Session storage is an optimization hint; update activation remains authoritative.
  }
}

async function downloadInitialSnapshot(): Promise<
  ReplicaSnapshotPageContract & { authorizedActor: AuthenticatedUserContract }
> {
  let firstPage:
    (ReplicaSnapshotPageContract & { authorizedActor: AuthenticatedUserContract }) | undefined;
  const students: ReplicaSnapshotPageContract["students"][number][] = [];

  for (let pageNumber = 1; pageNumber <= 100; pageNumber += 1) {
    const page = await json<
      ReplicaSnapshotPageContract & { authorizedActor: AuthenticatedUserContract }
    >(await deviceFetch(`/api/sync/snapshot?page=${pageNumber}&pageSize=${SNAPSHOT_PAGE_SIZE}`));
    firstPage ??= page;
    if (page.snapshotId !== firstPage.snapshotId || page.cursor !== firstPage.cursor) {
      throw new Error("Snapshot changed while the offline replica was being prepared");
    }
    students.push(...page.students);
    if (!page.hasMore) {
      return {
        ...firstPage,
        page: 1,
        hasMore: false,
        students,
      };
    }
  }

  throw new Error("Snapshot page limit exceeded");
}

/**
 * Presentation helper: the device line shown on the authorization card.
 * Derived from the user agent and the installation identity that already
 * exists — it registers nothing and reads no new source.
 */
export function deviceFingerprintLabel(installation: string | null): string {
  const agent = navigator.userAgent;
  const browser = /Edg\//.test(agent)
    ? "Edge"
    : /Firefox\//.test(agent)
      ? "Firefox"
      : /Chrome\//.test(agent)
        ? "Chrome"
        : /Safari\//.test(agent)
          ? "Safari"
          : "Browser";
  const system = /Windows/.test(agent)
    ? "Windows"
    : /Android/.test(agent)
      ? "Android"
      : /iPhone|iPad|iPod/.test(agent)
        ? "iOS"
        : /Mac OS X/.test(agent)
          ? "macOS"
          : /Linux/.test(agent)
            ? "Linux"
            : "—";
  const short = installation ? installation.replaceAll("-", "").slice(0, 6).toUpperCase() : null;
  return short ? `${browser} · ${system} · ${short}` : `${browser} · ${system}`;
}

/**
 * Whether an encrypted local copy already exists, so the sign-in screen can
 * offer the offline entrance only when it leads somewhere.
 */
export function useExistingReplica(): boolean {
  const [exists, setExists] = useState(false);
  useEffect(() => {
    let live = true;
    void localStore.hasReplica().then((value) => {
      if (live) setExists(value);
    });
    return () => {
      live = false;
    };
  }, []);
  return exists;
}

/* ------------------------------------------------------------------ */
/* Access (passkey sign-in and enrollment)                             */
/* ------------------------------------------------------------------ */

export type AccessMode = "login" | "enroll";
export type AccessNotice = "error" | "enrolled" | "existing-passkey" | null;
/** Presentation phase for the sign-in heading, button label and error card. */
export type AccessPhase = "idle" | "submitting" | "error";
export type AccessAuthenticatedHandler = (
  user: AuthenticatedUserContract,
  csrf: string,
  passkey: PasskeyUnlockMaterial | null,
) => void;

function passkeyPrfOutput(
  response: Awaited<ReturnType<typeof startAuthentication>>,
): Uint8Array<ArrayBuffer> | null {
  const extensions = response.clientExtensionResults as AuthenticationExtensionsClientOutputs & {
    prf?: { results?: { first?: ArrayBuffer } };
  };
  const output = extensions.prf?.results?.first;
  return output instanceof ArrayBuffer ? new Uint8Array(output) : null;
}

export interface AccessController {
  readonly mode: AccessMode;
  readonly identifier: string;
  readonly code: string;
  readonly busy: boolean;
  readonly phase: AccessPhase;
  readonly notice: AccessNotice;
  readonly registrationFallbackReady: boolean;
  readonly setMode: (mode: AccessMode) => void;
  readonly setIdentifier: (value: string) => void;
  readonly setCode: (value: string) => void;
  readonly submit: () => Promise<void>;
}

type RegistrationOptionsJSON = Parameters<typeof startRegistration>[0]["optionsJSON"];

function withoutPrfExtension(options: RegistrationOptionsJSON): RegistrationOptionsJSON {
  if (!options.extensions || !("prf" in options.extensions)) return options;
  const extensions = { ...options.extensions };
  delete extensions.prf;
  return { ...options, extensions };
}

function hasErrorName(error: unknown, name: string): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { readonly name?: unknown }).name === name
  );
}

async function prepareRegistrationOptions(
  options: RegistrationOptionsJSON,
  forceCompatible: boolean,
): Promise<{ options: RegistrationOptionsJSON; requestedPrf: boolean }> {
  if (forceCompatible) return { options: withoutPrfExtension(options), requestedPrf: false };

  const credential = globalThis.PublicKeyCredential as
    | (typeof PublicKeyCredential & {
        getClientCapabilities?: () => Promise<Record<string, boolean>>;
      })
    | undefined;
  if (typeof credential?.getClientCapabilities === "function") {
    try {
      const capabilities = await credential.getClientCapabilities();
      if (capabilities["extension:prf"] !== true)
        return { options: withoutPrfExtension(options), requestedPrf: false };
    } catch {
      // Capability discovery is advisory. Older clients can still ignore an
      // unsupported extension, and a failed ceremony enables the explicit
      // compatible retry below.
    }
  }
  return {
    options,
    requestedPrf: Boolean(options.extensions && "prf" in options.extensions),
  };
}

export function useAccess(onAuthenticated: AccessAuthenticatedHandler): AccessController {
  const [mode, setMode] = useState<AccessMode>("login");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<AccessNotice>(null);
  const [registrationFallbackReady, setRegistrationFallbackReady] = useState(false);

  useEffect(() => {
    let active = true;
    void lastAuthenticatedIdentifier()
      .then((value) => {
        if (active && value) setIdentifier((current) => current || value);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const submit = useCallback(async () => {
    setBusy(true);
    setNotice(null);
    try {
      if (mode === "enroll") {
        const start = await json<{
          flowId: string;
          options: RegistrationOptionsJSON;
        }>(
          await fetch("/api/auth/enrollment/options", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ code }),
          }),
        );
        const prepared = await prepareRegistrationOptions(start.options, registrationFallbackReady);
        let response: Awaited<ReturnType<typeof startRegistration>>;
        try {
          response = await startRegistration({ optionsJSON: prepared.options });
        } catch (error) {
          if (hasErrorName(error, "InvalidStateError")) {
            setIdentifier(start.options.user.name);
            setMode("login");
            setRegistrationFallbackReady(false);
            setNotice("existing-passkey");
            return;
          }
          if (prepared.requestedPrf) setRegistrationFallbackReady(true);
          throw error;
        }
        await json(
          await fetch("/api/auth/enrollment/verify", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ flowId: start.flowId, response }),
          }),
        );
        setMode("login");
        setRegistrationFallbackReady(false);
        setNotice("enrolled");
        return;
      }
      const start = await json<{ flowId: string; options: Record<string, unknown> }>(
        await fetch("/api/auth/authentication/options", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ identifier }),
        }),
      );
      const localUnlock = await localStore.passkeyUnlockConfiguration().catch(() => null);
      const allowedCredentials = Array.isArray(start.options.allowCredentials)
        ? (start.options.allowCredentials as readonly { readonly id?: unknown }[])
        : [];
      const preferredUnlock =
        localUnlock &&
        allowedCredentials.some((credential) => credential.id === localUnlock.credentialId)
          ? localUnlock
          : null;
      const extensions = start.options.extensions as
        { prf?: { eval?: { first?: string } } } | undefined;
      const first = extensions?.prf?.eval?.first;
      const prfSalt = preferredUnlock?.prfSalt ?? (first ? decodeBase64Url(first) : null);
      const preparedOptions = prfSalt
        ? {
            ...start.options,
            ...(preferredUnlock
              ? {
                  allowCredentials: allowedCredentials.filter(
                    (credential) => credential.id === preferredUnlock.credentialId,
                  ),
                }
              : {}),
            extensions: { prf: { eval: { first: prfSalt } } },
          }
        : start.options;
      const response = await startAuthentication({ optionsJSON: preparedOptions as never });
      const prfOutput = passkeyPrfOutput(response);
      const rpId = typeof start.options.rpId === "string" ? start.options.rpId : location.hostname;
      const passkey =
        prfSalt && prfOutput
          ? ({
              credentialId: response.id,
              rpId,
              prfSalt,
              prfOutput,
            } satisfies PasskeyUnlockMaterial)
          : null;
      const verified = await json<{ csrfToken: string; user: AuthenticatedUserContract }>(
        await fetch("/api/auth/authentication/verify", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ flowId: start.flowId, response }),
        }),
      );
      await rememberLastAuthenticatedIdentifier(verified.user.identifier).catch(() => undefined);
      onAuthenticated(verified.user, verified.csrfToken, passkey);
    } catch {
      setNotice("error");
    } finally {
      setBusy(false);
    }
  }, [code, identifier, mode, onAuthenticated, registrationFallbackReady]);

  const changeMode = useCallback((nextMode: AccessMode) => {
    setMode(nextMode);
    setNotice(null);
    setRegistrationFallbackReady(false);
  }, []);

  const changeIdentifier = useCallback((value: string) => {
    setIdentifier(value);
    setNotice(null);
  }, []);

  const changeCode = useCallback((value: string) => {
    setCode(value);
    setNotice(null);
    setRegistrationFallbackReady(false);
  }, []);

  return {
    mode,
    identifier,
    code,
    busy,
    phase: busy ? "submitting" : notice === "error" ? "error" : "idle",
    notice,
    registrationFallbackReady,
    setMode: changeMode,
    setIdentifier: changeIdentifier,
    setCode: changeCode,
    submit,
  };
}

/**
 * Single reader for the installation view of the session. Both the post
 * authentication gate decision and the "check again" re-poll go through it so
 * there is one description of what the server considers this browser to be.
 */
async function readInstallationSession(id: string): Promise<SessionContract> {
  return json<SessionContract>(
    await apiFetch(
      "/api/session",
      { headers: { "x-chams-installation": id } },
      SESSION_RESTORE_TIMEOUT_MS,
    ),
  );
}

async function provisionDeviceResumeToken(csrf: string): Promise<void> {
  const result = await json<{ token: string }>(
    await deviceFetch("/api/auth/device-resume-token", {
      method: "POST",
      headers: { "x-csrf-token": csrf },
    }),
  );
  await localStore.storeDeviceResumeToken(result.token);
}

async function resumeDeviceSession(token: string): Promise<SessionContract> {
  return json<SessionContract>(
    await deviceFetch("/api/auth/device-resume", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    }),
  );
}

async function endRevokedInstallationSession(csrf: string): Promise<void> {
  await localStore.invalidateOfflineAuthorization();
  await forgetInstallationIdentity();
  await apiFetch("/api/auth/logout", {
    method: "POST",
    headers: { "x-csrf-token": csrf },
  }).catch(() => undefined);
}

/* ------------------------------------------------------------------ */
/* Installation registration and authorization                         */
/* ------------------------------------------------------------------ */

export interface InstallationController {
  readonly installation: string | null;
  readonly pending: boolean;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly checking: boolean;
  readonly canAuthorize: boolean;
  readonly register: () => Promise<void>;
  readonly authorize: () => Promise<void>;
  readonly checkAgain: () => Promise<void>;
}

export function useInstallation(
  user: AuthenticatedUserContract,
  csrf: string,
  onReady: (csrf: string) => void,
): InstallationController {
  const [installation, setInstallation] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    void installationId().then((value) => {
      setInstallation(value);
      setPending(Boolean(value));
    });
  }, []);

  const register = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    try {
      const challenge = await json<{ challengeId: string; challenge: string }>(
        await fetch("/api/installations/challenge", {
          method: "POST",
          headers: { "x-csrf-token": csrf },
        }),
      );
      const publicKeySpki = await installationPublicKey();
      const signature = await signInstallationValue(
        `chams-installation-registration\n${challenge.challengeId}\n${challenge.challenge}`,
      );
      const result = await json<{ id: string }>(
        await fetch("/api/installations", {
          method: "POST",
          headers: { "content-type": "application/json", "x-csrf-token": csrf },
          body: JSON.stringify({
            challengeId: challenge.challengeId,
            publicKeySpki,
            signature,
            label: deviceFingerprintLabel(null),
          }),
        }),
      );
      await rememberInstallationId(result.id);
      await rememberLastAuthenticatedIdentifier(user.identifier).catch(() => undefined);
      setInstallation(result.id);
      setPending(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [csrf, user.identifier]);

  const authorize = useCallback(async () => {
    if (!installation) return;
    setBusy(true);
    setFailed(false);
    try {
      const result = await json<{ csrfToken: string; deviceCode: string }>(
        await deviceFetch(`/api/installations/${installation}/authorize`, {
          method: "POST",
          headers: { "x-csrf-token": csrf },
        }),
      );
      await rememberInstallationDeviceCode(result.deviceCode);
      onReady(result.csrfToken);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [csrf, installation, onReady]);

  const checkAgain = useCallback(async () => {
    if (!installation) return;
    setChecking(true);
    try {
      const session = await readInstallationSession(installation);
      if (session.installation?.status === "authorized") {
        if (session.installation.deviceCode)
          await rememberInstallationDeviceCode(session.installation.deviceCode);
        onReady(session.csrfToken);
      }
    } catch {
      /* still pending: the card keeps waiting rather than claiming a failure */
    } finally {
      setChecking(false);
    }
  }, [installation, onReady]);

  return {
    installation,
    pending,
    busy,
    failed,
    checking,
    canAuthorize: user.role === "system-administrator",
    register,
    authorize,
    checkAgain,
  };
}

/* ------------------------------------------------------------------ */
/* Offline replica preparation                                         */
/* ------------------------------------------------------------------ */

export interface OfflineSetupController {
  readonly pin: string;
  readonly confirm: string;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly mismatch: boolean;
  readonly valid: boolean;
  readonly setPin: (value: string) => void;
  readonly setConfirm: (value: string) => void;
  readonly submit: () => Promise<void>;
}

export function useOfflineSetup(
  onReady: (replica: StudentReplicaContract) => void | Promise<void>,
  passkey: PasskeyUnlockMaterial | null = null,
): OfflineSetupController {
  const [pin, setPinValue] = useState("");
  const [confirm, setConfirmValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [mismatch, setMismatch] = useState(false);
  const valid = /^\d{8}$/.test(pin) && pin === confirm;

  const setPin = useCallback((value: string) => {
    setMismatch(false);
    setPinValue(value);
  }, []);
  const setConfirm = useCallback((value: string) => {
    setMismatch(false);
    setConfirmValue(value);
  }, []);

  const submit = useCallback(async () => {
    if (!/^\d{8}$/.test(pin) || pin !== confirm) {
      setFailed(true);
      setMismatch(true);
      return;
    }
    setBusy(true);
    setFailed(false);
    setMismatch(false);
    try {
      const storageReadiness = await requestBrowserStorageReadiness();
      const snapshot = await downloadInitialSnapshot();
      await onReady(
        await localStore.initializeWithPinFromSnapshot(
          pin,
          snapshot,
          {
            ...storageReadiness,
            authorized: true,
          },
          passkey,
        ),
      );
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [confirm, onReady, passkey, pin]);

  return { pin, confirm, busy, failed, mismatch, valid, setPin, setConfirm, submit };
}

/* ------------------------------------------------------------------ */
/* PIN unlock                                                          */
/* ------------------------------------------------------------------ */

export type UnlockErrorCode = OfflineUnlockError["code"];

export interface UnlockController {
  readonly pin: string;
  readonly busy: boolean;
  readonly passkeyAvailable: boolean;
  readonly passkeyBusy: boolean;
  readonly error: string | null;
  /** The store's own reason, so the screen can pick its terminal treatment. */
  readonly errorCode: UnlockErrorCode | null;
  readonly setPin: (value: string) => void;
  readonly submit: () => Promise<void>;
  readonly submitPasskey: () => Promise<void>;
}

export function useUnlock(
  locale: Locale,
  onlineReauthorized: boolean,
  onUnlocked: (replica: StudentReplicaContract) => void | Promise<void>,
  pendingPasskey: PasskeyUnlockMaterial | null = null,
): UnlockController {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<UnlockErrorCode | null>(null);
  const passkeyInFlight = useRef(false);
  const passkeyPrompted = useRef(false);

  const submit = useCallback(async () => {
    setBusy(true);
    setError(null);
    setErrorCode(null);
    try {
      const replica = await (onlineReauthorized
        ? localStore.unlockWithPinAfterOnlineReauthorization(pin)
        : localStore.unlockWithPin(pin));
      if (pendingPasskey)
        await localStore.enablePasskeyUnlockWithPin(pin, pendingPasskey).catch(() => undefined);
      await onUnlocked(replica);
    } catch (reason) {
      const code = reason instanceof OfflineUnlockError ? reason.code : "CORRUPT_CACHE";
      setErrorCode(code);
      setError(
        translate(
          locale,
          code === "ONLINE_REAUTHORIZATION_REQUIRED"
            ? "offline.blocked"
            : code === "CORRUPT_CACHE"
              ? "offline.corrupt"
              : "offline.invalidPin",
        ),
      );
    } finally {
      setBusy(false);
    }
  }, [locale, onUnlocked, onlineReauthorized, pendingPasskey, pin]);

  const submitPasskey = useCallback(async () => {
    if (passkeyInFlight.current) return;
    passkeyInFlight.current = true;
    setPasskeyBusy(true);
    try {
      const configuration = await localStore.passkeyUnlockConfiguration();
      if (!configuration) {
        setPasskeyAvailable(false);
        return;
      }
      setPasskeyAvailable(true);
      const response = await startAuthentication({
        optionsJSON: {
          challenge: encodeBase64Url(crypto.getRandomValues(new Uint8Array(32))),
          rpId: configuration.rpId,
          timeout: 60_000,
          userVerification: "required",
          allowCredentials: [{ id: configuration.credentialId, type: "public-key" }],
          extensions: { prf: { eval: { first: configuration.prfSalt } } },
        } as never,
      });
      if (response.id !== configuration.credentialId) return;
      const output = passkeyPrfOutput(response);
      if (!output) return;
      await onUnlocked(await localStore.unlockWithPrf(output, onlineReauthorized));
    } catch (reason) {
      if (
        reason instanceof OfflineUnlockError &&
        reason.code === "ONLINE_REAUTHORIZATION_REQUIRED"
      ) {
        setErrorCode(reason.code);
        setError(translate(locale, "offline.blocked"));
      }
    } finally {
      passkeyInFlight.current = false;
      setPasskeyBusy(false);
    }
  }, [locale, onUnlocked, onlineReauthorized]);

  useEffect(() => {
    let active = true;
    void localStore
      .passkeyUnlockConfiguration()
      .then((configuration) => {
        if (!active) return;
        setPasskeyAvailable(Boolean(configuration));
        if (configuration && !passkeyPrompted.current) {
          passkeyPrompted.current = true;
          void submitPasskey();
        }
      })
      .catch(() => {
        if (active) setPasskeyAvailable(false);
      });
    return () => {
      active = false;
    };
  }, [submitPasskey]);

  return {
    pin,
    busy,
    passkeyAvailable,
    passkeyBusy,
    error,
    errorCode,
    setPin,
    submit,
    submitPasskey,
  };
}

/* ------------------------------------------------------------------ */
/* Application shell state                                             */
/* ------------------------------------------------------------------ */

export interface SessionController {
  readonly locale: Locale;
  readonly gate: Gate;
  readonly user: AuthenticatedUserContract | null;
  readonly csrf: string | null;
  readonly replica: StudentReplicaContract | null;
  readonly online: boolean;
  readonly serverSession: ServerSessionState;
  readonly onlineReauthorized: boolean;
  readonly pendingPasskey: PasskeyUnlockMaterial | null;
  readonly updateReady: boolean;
  readonly updateBlocked: boolean;
  readonly updateActivating: boolean;
  readonly toggleLocale: () => void;
  readonly lock: () => void;
  readonly logout: () => Promise<void>;
  readonly authenticated: (
    user: AuthenticatedUserContract,
    csrf: string,
    passkey: PasskeyUnlockMaterial | null,
  ) => Promise<void>;
  readonly installationReady: (csrf: string) => Promise<void>;
  readonly replicaReady: (replica: StudentReplicaContract) => void | Promise<void>;
  readonly unlocked: (replica: StudentReplicaContract) => Promise<void>;
  readonly useAnotherAccount: () => void;
  readonly retryServerSession: () => Promise<void>;
  readonly activateUpdate: () => Promise<void>;
}

export function useSession(): SessionController {
  const [locale, setLocale] = useState<Locale>("ar");
  const [gate, setGate] = useState<Gate>("loading");
  const [user, setUser] = useState<AuthenticatedUserContract | null>(null);
  const [csrf, setCsrf] = useState<string | null>(null);
  const [replica, setReplica] = useState<StudentReplicaContract | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [serverSession, setServerSession] = useState<ServerSessionState>("restoring");
  const [onlineReauthorized, setOnlineReauthorized] = useState(false);
  const [pendingPasskey, setPendingPasskey] = useState<PasskeyUnlockMaterial | null>(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [updateBlocked, setUpdateBlocked] = useState(false);
  const [updateActivating, setUpdateActivating] = useState(false);
  const lockTimer = useRef<number | null>(null);

  const lock = useCallback(() => {
    localStore.lock();
    setReplica(null);
    setGate("locked");
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = directionFor(locale);
  }, [locale]);

  useEffect(() => {
    const invalidate = () => {
      setUser(null);
      setCsrf(null);
      setServerSession("expired");
      if (replica) {
        localStore.lock();
        setReplica(null);
        setOnlineReauthorized(false);
        setPendingPasskey(null);
        setGate("locked");
      } else setGate("auth");
    };
    window.addEventListener(SERVER_SESSION_INVALID_EVENT, invalidate);
    return () => window.removeEventListener(SERVER_SESSION_INVALID_EVENT, invalidate);
  }, [replica]);

  useEffect(() => {
    const invalidate = () => {
      setUser(null);
      setCsrf(null);
      setReplica(null);
      setServerSession("expired");
      setOnlineReauthorized(false);
      setPendingPasskey(null);
      setGate("auth");
      void (async () => {
        if (csrf) await endRevokedInstallationSession(csrf);
        else {
          await localStore.invalidateOfflineAuthorization();
          await forgetInstallationIdentity();
        }
      })();
    };
    window.addEventListener(INSTALLATION_REVOKED_EVENT, invalidate);
    return () => window.removeEventListener(INSTALLATION_REVOKED_EVENT, invalidate);
  }, [csrf]);

  useEffect(() => {
    const ready = () => setUpdateReady(true);
    window.addEventListener(OFFLINE_UPDATE_READY_EVENT, ready);
    if ("serviceWorker" in navigator)
      void navigator.serviceWorker
        .getRegistration("/")
        .then((registration) => {
          if (registration?.waiting) ready();
        })
        .catch(() => undefined);
    return () => window.removeEventListener(OFFLINE_UPDATE_READY_EVENT, ready);
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    if (gate !== "workspace") return;
    const reset = () => {
      if (lockTimer.current) window.clearTimeout(lockTimer.current);
      lockTimer.current = window.setTimeout(lock, IDLE_LOCK_MS);
    };
    const events = ["pointerdown", "keydown", "touchstart"] as const;
    for (const event of events) window.addEventListener(event, reset, { passive: true });
    window.addEventListener("chams:lock", lock);
    reset();
    return () => {
      if (lockTimer.current) window.clearTimeout(lockTimer.current);
      for (const event of events) window.removeEventListener(event, reset);
      window.removeEventListener("chams:lock", lock);
    };
  }, [gate, lock]);

  const authenticated = useCallback(
    async (
      nextUser: AuthenticatedUserContract,
      nextCsrf: string,
      nextPasskey: PasskeyUnlockMaterial | null,
    ) => {
      setUser(nextUser);
      setCsrf(nextCsrf);
      setServerSession("ready");
      setPendingPasskey(nextPasskey);
      try {
        const id = await installationId();
        if (!id) {
          setGate("installation");
          return;
        }
        const session = await readInstallationSession(id);
        setCsrf(session.csrfToken);
        if (session.installation?.status !== "authorized") {
          if (session.installation?.status === "revoked") {
            setUser(null);
            setCsrf(null);
            setReplica(null);
            setServerSession("expired");
            setOnlineReauthorized(false);
            setPendingPasskey(null);
            setGate("auth");
            await endRevokedInstallationSession(session.csrfToken);
            return;
          }
          if (session.installation?.status !== "pending") await forgetInstallationIdentity();
          setGate("installation");
          return;
        }
        if (session.installation.deviceCode)
          await rememberInstallationDeviceCode(session.installation.deviceCode);
        const hasReplica = await localStore.hasReplica();
        if (hasReplica && nextPasskey) {
          try {
            const nextReplica = await localStore.unlockWithPrf(nextPasskey.prfOutput, true);
            await provisionDeviceResumeToken(session.csrfToken).catch(() => undefined);
            setReplica(nextReplica);
            setOnlineReauthorized(false);
            setPendingPasskey(null);
            setGate("workspace");
            return;
          } catch {
            // Fall back to the existing PIN/passkey unlock gate when this credential cannot unwrap.
          }
        }
        setOnlineReauthorized(hasReplica);
        setGate(hasReplica ? "locked" : "offline-setup");
      } catch (reason) {
        if (reason instanceof ApiRequestError && reason.kind === "authentication") {
          setUser(null);
          setCsrf(null);
          setServerSession("expired");
          setGate("auth");
          return;
        }
        setServerSession("unreachable");
        setGate("installation");
      }
    },
    [],
  );

  const installationReady = useCallback(async (nextCsrf: string) => {
    const hasReplica = await localStore.hasReplica();
    setCsrf(nextCsrf);
    setServerSession("ready");
    setOnlineReauthorized(hasReplica);
    setGate(hasReplica ? "locked" : "offline-setup");
  }, []);

  const logout = useCallback(async () => {
    if (csrf)
      await apiFetch("/api/auth/logout", {
        method: "POST",
        headers: { "x-csrf-token": csrf },
      }).catch(() => undefined);
    localStore.lock();
    setUser(null);
    setCsrf(null);
    setReplica(null);
    setServerSession("expired");
    setOnlineReauthorized(false);
    setPendingPasskey(null);
    setGate((await localStore.hasReplica()) ? "locked" : "auth");
  }, [csrf]);

  const restoreServerSession = useCallback(
    async (
      showLoading: boolean,
      destinations: { readonly ready?: Gate; readonly expired?: Gate } = {},
    ) => {
      const readyDestination = destinations.ready ?? "workspace";
      const expiredDestination = destinations.expired ?? "workspace";
      let destination = readyDestination;
      setServerSession("restoring");
      if (showLoading) setGate("loading");
      try {
        if (!navigator.onLine) {
          setUser(null);
          setCsrf(null);
          setServerSession("unreachable");
          return;
        }
        const id = await installationId();
        if (!id) {
          setUser(null);
          setCsrf(null);
          setServerSession("expired");
          destination = expiredDestination;
          return;
        }
        const resumeToken = await localStore.deviceResumeToken().catch(() => null);
        let restored: SessionContract;
        try {
          restored = await readInstallationSession(id);
        } catch (reason) {
          if (
            !resumeToken ||
            !(reason instanceof ApiRequestError) ||
            reason.kind !== "authentication"
          )
            throw reason;
          restored = await resumeDeviceSession(resumeToken);
        }
        setUser(restored.user);
        setCsrf(restored.csrfToken);
        setServerSession("ready");
        if (restored.installation?.status !== "authorized") {
          if (restored.installation?.status === "revoked") {
            setUser(null);
            setCsrf(null);
            setReplica(null);
            setServerSession("expired");
            setOnlineReauthorized(false);
            setPendingPasskey(null);
            destination = "auth";
            await endRevokedInstallationSession(restored.csrfToken);
            return;
          }
          if (restored.installation?.status !== "pending") await forgetInstallationIdentity();
          destination = "installation";
          return;
        }
        if (restored.installation.deviceCode)
          await rememberInstallationDeviceCode(restored.installation.deviceCode);
        if (!resumeToken)
          await provisionDeviceResumeToken(restored.csrfToken).catch(() => undefined);
      } catch (reason) {
        setUser(null);
        setCsrf(null);
        const expired = reason instanceof ApiRequestError && reason.kind === "authentication";
        setServerSession(expired ? "expired" : "unreachable");
        if (expired) destination = expiredDestination;
      } finally {
        if (showLoading || destination !== "workspace") setGate(destination);
      }
    },
    [],
  );

  useEffect(() => {
    if (!online || gate !== "workspace" || serverSession !== "unreachable") return;
    const retry = () => {
      if (document.visibilityState === "visible") void restoreServerSession(false);
    };
    retry();
    window.addEventListener("focus", retry);
    document.addEventListener("visibilitychange", retry);
    return () => {
      window.removeEventListener("focus", retry);
      document.removeEventListener("visibilitychange", retry);
    };
  }, [gate, online, restoreServerSession, serverSession]);

  useEffect(() => {
    let active = true;
    const restartedForUpdate = updateRestartRequested();
    void (async () => {
      try {
        const exists = await localStore.hasReplica();
        if (!active) return;
        if (!exists) {
          setServerSession("expired");
          setGate("auth");
          return;
        }
        if (!restartedForUpdate) {
          setServerSession("restoring");
          setGate("locked");
          return;
        }
        await restoreServerSession(true, { ready: "locked", expired: "locked" });
      } catch {
        if (active) {
          setServerSession("unreachable");
          setGate("auth");
        }
      } finally {
        if (active && restartedForUpdate) rememberUpdateRestart(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [restoreServerSession]);

  const activateUpdate = useCallback(async () => {
    const queue = await localStore.queueItems().catch(() => null);
    if (!queue || queue.some((item) => item.state === "syncing")) {
      setUpdateBlocked(true);
      return;
    }
    setUpdateBlocked(false);
    setUpdateActivating(true);
    rememberUpdateRestart(true);
    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), {
      once: true,
    });
    if (!(await activateWaitingOfflineUpdate().catch(() => false))) {
      rememberUpdateRestart(false);
      setUpdateActivating(false);
    }
  }, []);

  return {
    locale,
    gate,
    user,
    csrf,
    replica,
    online,
    serverSession,
    onlineReauthorized,
    pendingPasskey,
    updateReady,
    updateBlocked,
    updateActivating,
    toggleLocale: useCallback(() => setLocale((value) => (value === "ar" ? "fr" : "ar")), []),
    lock,
    logout,
    authenticated,
    installationReady,
    replicaReady: useCallback(
      async (next: StudentReplicaContract) => {
        if (csrf) await provisionDeviceResumeToken(csrf).catch(() => undefined);
        setReplica(next);
        setServerSession("ready");
        setPendingPasskey(null);
        setGate("workspace");
      },
      [csrf],
    ),
    unlocked: useCallback(
      async (next: StudentReplicaContract) => {
        setReplica(next);
        setOnlineReauthorized(false);
        setPendingPasskey(null);
        await restoreServerSession(true);
      },
      [restoreServerSession],
    ),
    useAnotherAccount: useCallback(() => {
      localStore.lock();
      setUser(null);
      setCsrf(null);
      setReplica(null);
      setServerSession("restoring");
      setGate("auth");
    }, []),
    retryServerSession: useCallback(() => restoreServerSession(false), [restoreServerSession]),
    activateUpdate,
  };
}
